import {
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  ListItem,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import ModuleLandingPage from "../../Components/ModuleLandingPage";

import TextField from "@material-ui/core/TextField";
import ChatIcon from "@material-ui/icons/Chat";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InfoIcon from "@material-ui/icons/Info";
import NotificationsIcon from "@material-ui/icons/Notifications";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";

import { useTranslation } from "react-i18next";
import {
  ECABIN,
  findValueInText,
  formatClientInfoString,
  formatTimeWithTZOffset,
  getInvoiceIssuerData,
  getInvoiceIssuerOptions,
  getInvoiceIssuers,
  getToken,
  getUser,
  invoiceIssuerMatchesValue,
  paymentStates,
  requestErrorHandler,
  searchMatchingList,
  shouldShowOrderSerialNoPreallocationLabel,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import EditOrder from "./EditOrder";
import NewOrder from "./NewOrder";

const server = EnvSettings.server;
const DEFAULT_ORDER_FILTER_STATES = [
  "waiting",
  "invoiced",
  "deposit",
  "completed",
  "transit",
];

const getInitialFilterStates = (orderId) =>
  orderId ? paymentStates.map((m) => m.value) : DEFAULT_ORDER_FILTER_STATES;

const mergeOrdersById = (currentOrders, newOrders) => {
  const ordersById = new Map();
  [...(currentOrders || []), ...(newOrders || [])].forEach((order) => {
    if (order?.id) {
      ordersById.set(order.id, order);
    }
  });
  return Array.from(ordersById.values());
};

const getFilterDateTime = (date, endOfDay) => {
  if (!date) {
    return undefined;
  }
  const parsedDate = new Date(
    date + (endOfDay ? "T23:59:59.999" : "T00:00:00"),
  );
  const time = parsedDate.getTime();
  return Number.isNaN(time) ? undefined : time;
};

const isDateInRange = (dateValue, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) {
    return true;
  }
  if (!dateValue) {
    return false;
  }
  const dateTime = new Date(dateValue).getTime();
  if (Number.isNaN(dateTime)) {
    return false;
  }
  const fromTime = getFilterDateTime(dateFrom);
  const toTime = getFilterDateTime(dateTo, true);
  return (
    (fromTime === undefined || dateTime >= fromTime) &&
    (toTime === undefined || dateTime <= toTime)
  );
};

const getOrderDisplayUpdatedAt = (order) => {
  if (order?.Notes && order.Notes.length) {
    const latestNote = order.Notes.reduce((prev, current) =>
      prev && prev.id > current.id ? prev : current,
    );
    return latestNote?.updatedAt;
  }
  return order?.activityUpdatedAt || order?.updatedAt;
};

const isWoocommerceOrder = (order) => {
  return !!order?.metadata?.wooOrderId;
};

const isHubspotOrder = (order) => {
  return !!order?.metadata?.dealId && !isWoocommerceOrder(order);
};

const orderSourceMatchesFilters = (order, filters) => {
  const sourceFilters = [];
  if (filters.woocommerce) {
    sourceFilters.push(isWoocommerceOrder(order));
  }
  if (filters.hubspot) {
    sourceFilters.push(isHubspotOrder(order));
  }
  return sourceFilters.length ? sourceFilters.some(Boolean) : true;
};

const getOrderStatistics = (orders) => {
  const itemsByKey = new Map();
  (orders || []).forEach((order) => {
    (Array.isArray(order?.genericDevices) ? order.genericDevices : []).forEach(
      (item) => {
        const name = item?.name || "";
        const sku = item?.sku || "";
        const key = sku || name;
        if (!key) {
          return;
        }
        const parsedCount = Number(item?.count);
        const count = Number.isFinite(parsedCount) ? parsedCount : 1;
        const parsedPrice = Number(item?.price ?? item?.listPrice ?? 0);
        const price = Number.isFinite(parsedPrice) ? parsedPrice : 0;
        const parsedDiscount = Number(item?.discount || 0);
        const discount = Number.isFinite(parsedDiscount) ? parsedDiscount : 0;
        const aggregatePrice = count * price * (1 - discount / 100);
        const current = itemsByKey.get(key) || {
          name,
          sku,
          count: 0,
          aggregatePrice: 0,
          averagePrice: 0,
        };
        current.count += count;
        current.aggregatePrice += aggregatePrice;
        current.averagePrice = current.count
          ? current.aggregatePrice / current.count
          : 0;
        itemsByKey.set(key, current);
      },
    );
  });

  return {
    orderCount: orders?.length || 0,
    itemStats: Array.from(itemsByKey.values()).sort(
      (a, b) => b.count - a.count,
    ),
  };
};

export default function Orders(props) {
  const order_id = props?.computedMatch?.params?.order_id;

  const [searchList, setSearchList] = useState();
  const [orderList, setOrderList] = useState();
  const [searchValue, setSearchValue] = useState(() => order_id || "");
  const [orderFilters, setOrderFilters] = useState({});
  const [expandOrderItems, setExpandOrderItems] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [showOrderStatistics, setShowOrderStatistics] = useState(false);
  const [invoiceIssuers, setInvoiceIssuers] = useState([]);
  const [filterStates, setFilterStates] = useState(() =>
    getInitialFilterStates(order_id),
  );
  const [fetchedFilterStates, setFetchedFilterStates] = useState([]);
  const [loadingFilterStates, setLoadingFilterStates] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const editBoxRef = useRef(null);

  const updateOrderFilter = (key, value) => {
    setOrderFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value || undefined,
    }));
  };

  const orderStatistics = useMemo(
    () => getOrderStatistics(searchList),
    [searchList],
  );

  useEffect(() => {
    getInvoiceIssuers(setInvoiceIssuers, dispatch);
  }, [dispatch]);

  const checkForComments = (order) => {
    if (order?.hasComments || order?.Notes?.find((f) => f.User)) {
      return (
        <Grid item xs={6}>
          <Tooltip title={t("activeComments")}>
            <ChatIcon color="primary" style={{ fontSize: 20 }} />
          </Tooltip>
        </Grid>
      );
    }
    return null;
  };

  const checkForEmailSubscription = (order) => {
    if (
      order?.isSubscribed ||
      order.EmailListOrders?.find(
        (f) =>
          f &&
          f.EmailList?.EmailListUsers.find(
            (u) => u.User.id === getUser().userId,
          ),
      ) ||
      order.UserId === getUser().userId
    ) {
      return (
        <Grid item xs={6}>
          <Tooltip title={t("tagged")}>
            <NotificationsIcon color="primary" style={{ fontSize: 20 }} />
          </Tooltip>
        </Grid>
      );
    }
    return null;
  };

  const checkHubspotIntegration = (order) => {
    if (isHubspotOrder(order)) {
      return (
        <Grid item xs={6}>
          <Tooltip
            title={
              t("orders.createdFromHubspot") + " " + order?.metadata?.dealId
            }
          >
            <svg
              viewBox="0 0 16 16"
              height="20px"
              data-test-id="icon-hubspot-logo"
              role="img"
              aria-label="HubSpot logo"
            >
              <title>HubSpot</title>
              <path
                d="M11.6127 10.9174C10.4763 10.9174 9.55512 10.0374 9.55512 8.95201C9.55512 7.86645 10.4763 6.98646 11.6127 6.98646C12.749 6.98646 13.6702 7.86645 13.6702 8.95201C13.6702 10.0374 12.749 10.9174 11.6127 10.9174ZM12.2286 5.16801V3.41954C12.7064 3.20397 13.041 2.74229 13.041 2.20648V2.16612C13.041 1.42664 12.4077 0.821619 11.6336 0.821619H11.5915C10.8174 0.821619 10.1841 1.42664 10.1841 2.16612V2.20648C10.1841 2.74229 10.5187 3.20416 10.9965 3.41972V5.16801C10.2852 5.27306 9.63527 5.55332 9.09927 5.96578L4.07384 2.23138C4.107 2.10973 4.1303 1.9845 4.1305 1.85286C4.13129 1.0155 3.42174 0.335606 2.54479 0.334474C1.66822 0.333532 0.956311 1.01154 0.955323 1.84909C0.954337 2.68665 1.66388 3.36654 2.54084 3.36748C2.82651 3.36786 3.09106 3.29035 3.32283 3.16436L8.26614 6.83803C7.84582 7.44418 7.59944 8.17028 7.59944 8.95201C7.59944 9.77033 7.8701 10.5274 8.32734 11.1499L6.82415 12.5861C6.7053 12.5519 6.58211 12.5282 6.45141 12.5282C5.73101 12.5282 5.14684 13.086 5.14684 13.7742C5.14684 14.4626 5.73101 15.0205 6.45141 15.0205C7.17201 15.0205 7.75599 14.4626 7.75599 13.7742C7.75599 13.6498 7.73112 13.5319 7.69538 13.4184L9.18238 11.9978C9.85738 12.4899 10.698 12.7856 11.6127 12.7856C13.8292 12.7856 15.6257 11.0692 15.6257 8.95201C15.6257 7.03531 14.1517 5.45185 12.2286 5.16801Z"
                fill="#3d57a5"
              ></path>
            </svg>
          </Tooltip>
        </Grid>
      );
    }
    return null;
  };

  const checkWoocommerceIntegration = (order) => {
    if (order?.metadata?.wooOrderId) {
      return (
        <Grid item xs={6}>
          <Tooltip
            title={
              t("orders.createdFromWoocommerce") +
              " " +
              order?.metadata?.wooOrderId
            }
          >
            <svg
              viewBox="0 -51.5 256 256"
              width="30px"
              height="20px"
              data-test-id="icon-woocommerce-logo"
              role="img"
              aria-label="Woocommerce logo"
            >
              <title>Woocommerce</title>
              <g fill-rule="evenodd">
                <path
                  d="M232.137523,0 L23.7592719,0 C10.5720702,0 -0.103257595,10.7799647 0.000639559736,23.862498 L0.000639559736,103.404247 C0.000639559736,116.591115 10.6767385,127.266612 23.8639402,127.266612 L122.558181,127.266612 L167.667206,152.384995 L157.410382,127.266612 L232.137523,127.266612 C245.325059,127.266612 255.999888,116.591115 255.999888,103.404247 L255.999888,23.862498 C255.999888,10.6752964 245.325059,0 232.137523,0 Z M19.3640061,18.4201481 C16.4334946,18.6294847 14.2355942,19.6761007 12.7703719,21.6645976 C11.3051496,23.5484931 10.781875,25.9556632 11.095813,28.6768382 C17.2707741,67.9247058 23.027062,94.4034429 28.3647436,108.113986 C30.4579757,113.137395 32.8651458,115.5451 35.690989,115.335764 C40.086656,115.021425 45.3196694,108.951332 51.4946305,97.1248185 C54.73908,90.4260478 59.7628237,80.3792294 66.5657276,66.9823567 C72.2170797,86.762992 79.9618646,101.625221 89.6956814,111.567705 C92.417057,114.393415 95.2427665,115.649434 97.9634733,115.440098 C100.371178,115.230761 102.254539,113.974742 103.510558,111.672039 C104.557241,109.683676 104.975914,107.380974 104.766578,104.764601 C104.138568,95.2407893 105.080917,81.9489193 107.69729,64.8892584 C110.417997,47.3063898 113.767382,34.6424627 117.849111,27.1069476 C118.686458,25.5370569 119.000128,23.9670994 118.895794,22.0832708 C118.686458,19.6760338 117.639775,17.6875369 115.651411,16.1176463 C113.663048,14.5477557 111.46468,13.8151445 109.057643,14.0244142 C106.022597,14.2337508 103.719895,15.6989731 102.150205,18.6294847 C95.6614396,30.4560654 91.0560348,49.6088916 88.335328,76.1924977 C84.3579328,66.145211 81.0085475,54.3185634 78.3921746,40.3987506 C77.2411578,34.2238564 74.4154483,31.2933449 69.8100434,31.6073497 C66.670329,31.8166194 64.0538892,33.9098515 61.9606572,37.8869122 L39.0401069,81.5302462 C35.2723159,66.3544807 31.7138615,47.8296643 28.4694119,25.9556632 C27.7368007,20.5133802 24.7016209,18.0014749 19.3640061,18.4201481 Z M221.044022,25.9559976 C228.475136,27.5259551 234.022221,31.5030159 237.789611,38.0965832 C241.138996,43.7482697 242.81302,50.5511737 242.81302,58.7146317 C242.81302,69.4943957 240.092314,79.3325464 234.649562,88.3333508 C228.370134,98.7995112 220.206676,104.032257 210.054854,104.032257 C208.275828,104.032257 206.391799,103.82292 204.402767,103.404247 C196.972321,101.834557 191.425236,97.857831 187.657177,91.264063 C184.308461,85.5076413 182.633768,78.6002028 182.633768,70.5410787 C182.633768,59.7612478 185.355144,49.923164 190.797226,41.0270948 C197.181658,30.5610681 205.345116,25.3280548 215.392603,25.3280548 C217.171629,25.3280548 219.055659,25.5373913 221.044022,25.9559976 Z M216.648622,82.5769291 C220.521015,79.1232099 223.137388,73.9947979 224.602744,67.0873594 C225.021417,64.6802562 225.335087,62.0637496 225.335087,59.3425746 C225.335087,56.3074616 224.707078,53.0630121 223.451058,49.8184957 C221.881368,45.7367667 219.788002,43.5389332 217.275963,43.0155917 C213.508574,42.2829805 209.845518,44.3762126 206.391799,49.5045577 C203.566089,53.4816184 201.786394,57.6680157 200.844713,61.9590813 C200.321038,64.3663182 200.111701,66.9830255 200.111701,69.5993984 C200.111701,72.6344445 200.739711,75.8788272 201.99573,79.1232099 C203.566089,83.2049389 205.658786,85.4026386 208.170825,85.9263145 C210.787198,86.4493215 213.612908,85.2983047 216.648622,82.5769291 Z M172.167608,38.0965832 C168.399549,31.5030159 162.74813,27.5259551 155.422019,25.9559976 C153.432987,25.5373913 151.549626,25.3280548 149.769931,25.3280548 C139.723112,25.3280548 131.559654,30.5610681 125.175223,41.0270948 C119.732472,49.923164 117.011765,59.7612478 117.011765,70.5410787 C117.011765,78.6002028 118.686458,85.5076413 122.035174,91.264063 C125.803233,97.857831 131.350318,101.834557 138.780763,103.404247 C140.769126,103.82292 142.653156,104.032257 144.432851,104.032257 C154.584672,104.032257 162.74813,98.7995112 169.027559,88.3333508 C174.47031,79.3325464 177.191017,69.4943957 177.191017,58.7146317 C177.191017,50.5511737 175.516324,43.7482697 172.167608,38.0965832 Z M158.980072,67.0873594 C157.515384,73.9947979 154.898343,79.1232099 151.02595,82.5769291 C147.990904,85.2983047 145.165195,86.4493215 142.548822,85.9263145 C140.036783,85.4026386 137.943417,83.2049389 136.373727,79.1232099 C135.117707,75.8788272 134.489698,72.6344445 134.489698,69.5993984 C134.489698,66.9830255 134.699034,64.3663182 135.22271,61.9590813 C136.16439,57.6680157 137.943417,53.4816184 140.769126,49.5045577 C144.223514,44.3762126 147.88657,42.2829805 151.65396,43.0155917 C154.165999,43.5389332 156.259365,45.7367667 157.829055,49.8184957 C159.085074,53.0630121 159.713084,56.3074616 159.713084,59.3425746 C159.713084,62.0637496 159.503748,64.6802562 158.980072,67.0873594 Z"
                  fill="#7F54B3"
                ></path>
              </g>
            </svg>
          </Tooltip>
        </Grid>
      );
    }
    return null;
  };

  const iconBox = (data, d, dd) => {
    var comments = checkForComments(dd);
    var emails = checkForEmailSubscription(dd);
    var woocommerce = checkWoocommerceIntegration(dd);
    var hubspot = checkHubspotIntegration(dd);
    if (!comments && !emails && !hubspot && !woocommerce) {
      return "";
    }
    return (
      <Grid container spacing={1}>
        {comments}
        {emails}
        {hubspot}
        {woocommerce}
      </Grid>
    );
  };

  const executeScroll = () => {
    editBoxRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const showOrder = (dev) => {
    setSelectedOrder();
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "detail",
        order_id: dev.id,
      }),
    };
    fetch(server + "/get_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          // Hack for refreshing the selected device component
          setTimeout(() => {
            setSelectedOrder(result.orders?.[0] || dev);
            if (editBoxRef && editBoxRef.current) {
              executeScroll();
            }
          }, 1);
        }
      })
      .catch(() => {
        setTimeout(() => {
          setSelectedOrder(dev);
          if (editBoxRef && editBoxRef.current) {
            executeScroll();
          }
        }, 1);
      });
  };

  const getOrders = (statesToFetch, replace) => {
    const paymentStatesToFetch = Array.isArray(statesToFetch)
      ? statesToFetch.filter(Boolean)
      : filterStates;
    if (!paymentStatesToFetch.length) {
      if (replace) {
        setOrderList([]);
        setSearchList([]);
        setFetchedFilterStates([]);
      }
      return;
    }
    setLoading(true);
    setLoadingFilterStates((currentStates) => [
      ...new Set([...currentStates, ...paymentStatesToFetch]),
    ]);
    if (replace) {
      setSearchList();
      setSelectedOrder();
    }
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "list",
        paymentStates: paymentStatesToFetch,
      }),
    };
    fetch(server + "/get_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setOrderList((currentOrders) =>
            replace
              ? result.orders
              : mergeOrdersById(currentOrders, result.orders),
          );
          setFetchedFilterStates((currentStates) =>
            replace
              ? paymentStatesToFetch
              : [...new Set([...currentStates, ...paymentStatesToFetch])],
          );
        }
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !paymentStatesToFetch.includes(state),
          ),
        );
      })
      .catch((e) => {
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !paymentStatesToFetch.includes(state),
          ),
        );
      });
  };

  useEffect(() => {
    getOrders(getInitialFilterStates(order_id), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterStateChange = (state, selected) => {
    const nextStates = selected
      ? [...new Set([...filterStates, state])]
      : filterStates.filter((filterState) => filterState !== state);
    setFilterStates(nextStates);
    if (selected && !fetchedFilterStates.includes(state)) {
      getOrders([state], false);
    }
  };

  const filterResults = () => {
    setSearchList(
      orderList &&
        orderList.filter((contact) => {
          return (
            filterStates.find((o) => o === contact.paymentStatus) &&
            orderSourceMatchesFilters(contact, orderFilters) &&
            searchMatchingList(
              [
                contact.number,
                contact.invoiceNumbers,
                contact.clientInfo,
                contact.genericDevices,
                contact.Devices,
                contact.User,
                contact.metadata,
              ],
              searchValue,
            ) &&
            invoiceIssuerMatchesValue(
              contact.invoiceIssuer,
              orderFilters.invoiceIssuer,
            ) &&
            findValueInText(contact.User?.fullName, orderFilters.createdBy) &&
            (findValueInText(contact.genericDevices, orderFilters.items) ||
              findValueInText(contact.Devices, orderFilters.items)) &&
            isDateInRange(
              contact.createdAt,
              orderFilters.createdFrom,
              orderFilters.createdTo,
            ) &&
            isDateInRange(
              getOrderDisplayUpdatedAt(contact),
              orderFilters.updatedFrom,
              orderFilters.updatedTo,
            )
          );
        }),
    );
  };
  useEffect(filterResults, [
    searchValue,
    orderList,
    filterStates,
    orderFilters.invoiceIssuer,
    orderFilters.createdBy,
    orderFilters.items,
    orderFilters.createdFrom,
    orderFilters.createdTo,
    orderFilters.updatedFrom,
    orderFilters.updatedTo,
    orderFilters.woocommerce,
    orderFilters.hubspot,
    invoiceIssuers,
  ]);

  return (
    <ModuleLandingPage
      title={t("orders.header")}
      actions={<NewOrder />}
      filters={
        <>
          <Grid item xs={12} sm={3}>
            <TextField
              autoFocus
              label={t("search")}
              type="text"
              fullWidth
              variant="outlined"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value.toLowerCase());
              }}
              ref={editBoxRef}
            />
          </Grid>
          <Grid item container spacing={1} xs={12} alignItems="center">
            <Grid item>
              <Title>{t("quickFilters")}</Title>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowAdditionalFilters(!showAdditionalFilters);
                }}
              >
                {showAdditionalFilters ? t("hide") : t("show")}
              </Button>
            </Grid>
          </Grid>
          {showAdditionalFilters ? (
            <>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.invoiceIssuer")}
                  variant="outlined"
                  select
                  fullWidth
                  value={orderFilters.invoiceIssuer || ""}
                  onChange={(e) => {
                    updateOrderFilter("invoiceIssuer", e.target.value);
                  }}
                >
                  {[
                    { name: "", data: { name: t("select") } },
                    ...getInvoiceIssuerOptions(invoiceIssuers),
                  ].map((m, i) => (
                    <MenuItem key={(m.name || "select") + i} value={m.name}>
                      {m?.data?.name || t("select")}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.createdFrom")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.createdFrom || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateOrderFilter("createdFrom", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.createdTo")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.createdTo || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateOrderFilter("createdTo", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.updatedFrom")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.updatedFrom || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateOrderFilter("updatedFrom", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.updatedTo")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.updatedTo || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateOrderFilter("updatedTo", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.createdBy")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.createdBy || ""}
                  onChange={(e) => {
                    updateOrderFilter("createdBy", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.itemsFilter")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.items || ""}
                  onChange={(e) => {
                    updateOrderFilter("items", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={expandOrderItems}
                      onChange={(e, checked) => {
                        setExpandOrderItems(checked);
                      }}
                    />
                  }
                  label={t("orders.expandItems")}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={!!orderFilters.woocommerce}
                      onChange={(e, checked) => {
                        updateOrderFilter("woocommerce", checked);
                      }}
                    />
                  }
                  label={t("orders.woocommerceOrders")}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={!!orderFilters.hubspot}
                      onChange={(e, checked) => {
                        updateOrderFilter("hubspot", checked);
                      }}
                    />
                  }
                  label={t("orders.hubspotOrders")}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item>
                    <Typography style={{ fontWeight: "bold" }}>
                      {t("orders.statistics")}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography>
                      {t("orders.filteredOrders")}: {orderStatistics.orderCount}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowOrderStatistics(!showOrderStatistics);
                      }}
                    >
                      {showOrderStatistics ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Grid>
                </Grid>
                <Collapse in={showOrderStatistics}>
                  <Grid container spacing={2} style={{ marginTop: 4 }}>
                    <Grid item xs={12} md={6}>
                      {orderStatistics.itemStats.length ? (
                        <SortableTable
                          key={
                            orderStatistics.orderCount +
                            "-" +
                            orderStatistics.itemStats.length
                          }
                          headCells={[
                            {
                              id: "name",
                              numeric: false,
                              disablePadding: false,
                              label: t("item"),
                            },
                            {
                              id: "sku",
                              numeric: false,
                              disablePadding: false,
                              label: t("orders.sku"),
                            },
                            {
                              id: "count",
                              numeric: true,
                              disablePadding: false,
                              label: t("orders.allocatedCount"),
                            },
                            {
                              id: "aggregatePrice",
                              numeric: true,
                              disablePadding: false,
                              label: t("orders.aggregatePrice"),
                            },
                            {
                              id: "averagePrice",
                              numeric: true,
                              disablePadding: false,
                              label: t("orders.averagePrice"),
                            },
                          ]}
                          tableData={orderStatistics.itemStats}
                          header={
                            t("orders.order") + " " + t("orders.statistics")
                          }
                          downloadButton={true}
                          defaultSortRow={"count"}
                          defaultOrder={"desc"}
                          disablePagination={true}
                          rowsPerPage={orderStatistics.itemStats.length || 1}
                          tableSize={"small"}
                          handleRowClick={() => {}}
                          specialDataFormatting={[
                            {
                              id: "aggregatePrice",
                              formatReturnsElement: true,
                              format: (data) => Number(data || 0).toFixed(2),
                            },
                            {
                              id: "averagePrice",
                              formatReturnsElement: true,
                              format: (data) => Number(data || 0).toFixed(2),
                            },
                          ]}
                        />
                      ) : (
                        <Typography>
                          {t("orders.noItemsInFilteredOrders")}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Collapse>
              </Grid>
            </>
          ) : null}
          {searchList ? (
            <Grid container item xs={12}>
              <Grid item xs={11}>
                <Typography>{t("orders.paymentStatus")}</Typography>
                {paymentStates.map((o) => {
                  const isStateLoading = loadingFilterStates.includes(o.value);
                  return (
                    <FormControlLabel
                      key={o.label}
                      control={
                        <Checkbox
                          color="primary"
                          disabled={isStateLoading}
                          style={{
                            color: o.color || "",
                          }}
                          onChange={(e, v) => {
                            handleFilterStateChange(o.value, v);
                          }}
                          checked={!!filterStates.find((f) => f === o.value)}
                        />
                      }
                      label={
                        <>
                          {t("paymentStates." + o.value)}
                          {isStateLoading ? (
                            <CircularProgress
                              size={12}
                              style={{ marginLeft: 6 }}
                            />
                          ) : null}
                        </>
                      }
                    />
                  );
                })}
              </Grid>
              <Grid item xs={1}>
                <Typography style={{ textAlign: "center" }}>
                  Backlog Total{" "}
                  {searchList
                    .reduce(
                      (partialSum, a) =>
                        a.paymentStatus === "invoiced" ||
                        a.paymentStatus === "deposit"
                          ? partialSum + a.totalPrice - a.depositAmount
                          : partialSum,
                      0,
                    )
                    .toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          ) : null}
        </>
      }
      dialogs={
        <>
          <EditOrder selectedOrder={selectedOrder} />
        </>
      }
    >
      {searchList ? (
        <>
          <SortableTable
            header={t("orders.header")}
            tableLayout={"fixed"}
            downloadButton={true}
            headCells={[
              {
                id: "Notes",
                label: "",
                width: 70,
                hideFromCsv: true,
              },
              {
                id: "updatedAt",
                numeric: false,
                disablePadding: false,
                label: t("updatedAt"),
                // width: 105,
              },
              {
                id: "createdAt",
                numeric: false,
                disablePadding: false,
                label: t("createdAt"),
                // width: 105,
              },
              {
                id: "User.fullName",
                numeric: false,
                disablePadding: false,
                label: t("createdBy"),
              },
              {
                id: "invoiceIssuer",
                numeric: false,
                disablePadding: false,
                label: t("orders.invoiceIssuer"),
              },
              {
                id: "number",
                numeric: false,
                disablePadding: false,
                label: t("orders.orderNumber"),
              },
              {
                id: "invoiceNumbers",
                numeric: false,
                disablePadding: false,
                label: t("orders.invoiceNumbers"),
              },
              {
                id: "paymentStatus",
                numeric: false,
                disablePadding: false,
                label: t("orders.paymentStatus"),
              },
              {
                id: "clientInfo",
                numeric: false,
                disablePadding: false,
                label: t("clientInfo"),
              },
              {
                id: "genericDevices",
                numeric: false,
                disablePadding: false,
                label: t("items"),
              },
              {
                id: "Devices",
                numeric: false,
                disablePadding: false,
                label: t("orders.allocatedSerialNumbers"),
              },
              {
                id: "depositAmount",
                numeric: false,
                disablePadding: false,
                label: "Backlog",
              },
            ]}
            // disablePagination: true,
            defaultSortRow={"updatedAt"}
            handleRowClick={showOrder}
            getRowStyle={(data) => {
              var obj = { cursor: "pointer" };
              var p = paymentStates.find((f) => data.paymentStatus === f.value);
              if (p && p.color) {
                return { ...obj, backgroundColor: p.color };
              }
              return obj;
            }}
            specialDataFormatting={[
              {
                id: "updatedAt",
                format: (data, d, dd) => {
                  if (dd.Notes && dd.Notes.length) {
                    const max = dd.Notes.reduce((prev, current) =>
                      prev && prev.id > current.id ? prev : current,
                    );
                    return formatTimeWithTZOffset(max.updatedAt, ECABIN);
                  }
                  if (dd.activityUpdatedAt) {
                    return formatTimeWithTZOffset(dd.activityUpdatedAt, ECABIN);
                  }
                  return formatTimeWithTZOffset(data, ECABIN);
                },
              },
              {
                id: "Notes",
                format: iconBox,
              },
              {
                id: "createdAt",
                format: (data) => {
                  return formatTimeWithTZOffset(data, ECABIN);
                },
              },
              {
                id: "clientInfo",
                format: (data) => {
                  return formatClientInfoString(data, [
                    "companyName",
                    "contactName",
                    "location",
                  ]);
                },
              },
              {
                id: "genericDevices",
                format: (data, dataFormat) => {
                  const items = Array.isArray(data) ? data : [];
                  const formatItemsForCsv = (orderItems) =>
                    orderItems
                      .map((v) =>
                        [v.count, "x", v.name, v.sku ? "(" + v.sku + ")" : ""]
                          .filter(Boolean)
                          .join(" "),
                      )
                      .join(" | ");
                  if (dataFormat === "json") {
                    return formatItemsForCsv(items);
                  }
                  const renderItems = (orderItems, highlight) => (
                    <>
                      {orderItems.map((v, i) => (
                        <Typography
                          key={(v.sku || v.name || "item") + i}
                          style={{
                            ...(highlight ? { backgroundColor: "yellow" } : {}),
                            fontSize: 15,
                          }}
                        >
                          {v.count + " x " + v.name}
                        </Typography>
                      ))}
                    </>
                  );
                  var x;
                  const itemFilterValue = orderFilters.items || searchValue;
                  if (itemFilterValue && itemFilterValue !== "") {
                    x = items.filter(
                      (f) =>
                        (f.name || "")
                          .toLowerCase()
                          .includes(itemFilterValue.toLowerCase()) ||
                        (f.sku || "")
                          .toLowerCase()
                          .includes(itemFilterValue.toLowerCase()),
                    );
                    if (x.length) {
                      return renderItems(x, true);
                    }
                  }
                  if (items.length > 1) {
                    if (expandOrderItems) {
                      return renderItems(items);
                    }
                    x = 0;
                    items.forEach((d) => (x = x + d.count));
                    return t("count") + ":" + x;
                  }
                  var a = "";
                  items.forEach((v) => (a = v.count + " x " + v.name));
                  return a;
                },
              },
              {
                id: "Devices",
                format: (data, d, dd) => {
                  const allocationNeeded =
                    shouldShowOrderSerialNoPreallocationLabel(dd);
                  if (data && data.length) {
                    const serials = data.map((m) => m.serialNumber).join(" ");
                    return allocationNeeded
                      ? serials + " " + t("orders.allocationNeeded")
                      : serials;
                  }
                  if (allocationNeeded) {
                    return t("orders.allocationNeeded");
                  }
                  return "";
                },
              },
              {
                id: "paymentStatus",
                formatReturnsElement: true,
                format: (data, e, v) => {
                  if (e === "json") {
                    return t(
                      "paymentStates." +
                        paymentStates.find((o) => o.value === data).value,
                    );
                  }
                  return (
                    <ListItem>
                      {t(
                        "paymentStates." +
                          paymentStates.find((o) => o.value === data).value,
                      )}
                      {data === "transit" && v.multipleShipments ? (
                        <Tooltip
                          title={t("orders.multipleShipments")}
                          placement="top"
                        >
                          <ListItemIcon size="small">
                            <InfoIcon
                              style={{ verticalAlign: "middle" }}
                              fontSize="small"
                            />
                          </ListItemIcon>
                        </Tooltip>
                      ) : null}
                    </ListItem>
                  );
                },
              },
              {
                id: "invoiceIssuer",
                format: (data) => {
                  if (!data) {
                    return "";
                  }
                  return (
                    getInvoiceIssuerData(invoiceIssuers, data)?.name || data
                  );
                },
              },
              {
                id: "invoiceNumbers",
                formatReturnsElement: true,
                format: (data) => {
                  if (!data) {
                    return "";
                  }
                  if (data.length) {
                    return data.map((m) => (
                      <Typography style={{ fontSize: 14, marginTop: 2 }}>
                        {m}
                      </Typography>
                    ));
                  }
                  return data;
                },
              },
              {
                id: "depositAmount",
                format: (data, d, row) => {
                  if (
                    !(
                      row.paymentStatus === "invoiced" ||
                      row.paymentStatus === "deposit"
                    )
                  ) {
                    return "";
                  }
                  return row.totalPrice - row.depositAmount;
                },
              },
            ]}
            // handleScrollClick: showDeviceNewTab,
            tableData={searchList}
            rowsPerPage={50}
          />
        </>
      ) : loading ? (
        <CircularProgress />
      ) : undefined}
    </ModuleLandingPage>
  );
}
